import {
  Avatar,
  Box,
  Text,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import {
  FaBookmark,
  FaComment,
  FaHeart,
  FaRegBookmark,
  FaRegComment,
  FaRegHeart,
  FaShareAlt,
  FaPen,
  FaTrash,
  FaEllipsisV,
  FaDotCircle,
} from 'react-icons/fa';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { mutateFunction } from '../../libs/apis';
import CreateCommentModal from '../modal/CreateCommentModal';
import CreateReplyModal from '../modal/CreateReplyModal';
import { CustomToast } from '../toast/CustomToast';
import { useState, useEffect } from 'react';
import ShareContentModal from '../modal/ShareContentModal';
import { useSelector } from 'react-redux';
import moment from 'moment';
import DeleteCommentModal from '../modal/DeleteCommentModal';
import { useQueryClient } from 'react-query';
import EditCommentModal from '../modal/EditCommentModal';
import ReactionUsers from './ReactionUsersModal';
import useData from '../../data/useData';
import ReactionDisplay from './ReactonDisplay';
import CreateReply from './CreateReply';

const Comment = ({
  post,
  parentId,
  onCreateReply = () => {},
  postLink = '',
  queryKey,
  onCommentSuccess = () => {},
}) => {
  const { mutate, isLoading } = useMutation(mutateFunction);
  const userInfo = useSelector((state) => state?.user?.userInfo);
  const { addToast } = CustomToast();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isOpen: createCommentModalIsOpen,
    onClose: onCloseCreateCommentModal,
    onOpen: onOpenCreateCommentModal,
  } = useDisclosure();

  const {
    isOpen: deleteCommentModalIsOpen,
    onClose: onCloseDeleteCommentModal,
    onOpen: onOpenDeleteCommentModal,
  } = useDisclosure();

  const queryClient = useQueryClient();

  const [isPublic, setIsPublic] = useState('');

  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [params, setParams] = useState(null);
  const [latestReactionUser, setLatestReactionUser] = useState(null);

  const {
    data: reactionUsersData,
    isLoading: reactionUsersLoading,
    refetch: refetchReactionUsers,
  } = useData(
    `react/post/users?post_id=${parentId}&comment_id=${post.id}`,
    30000,
    {},
    1,
    false,
  );

  useEffect(() => {
    if (reactionUsersData) {
      setReactionUsers(reactionUsersData?.data?.reactionUsers);
    }
  }, [reactionUsersData]);

  const [reactionUsers, setReactionUsers] = useState([]);

  const {
    isOpen: reactionUsersIsOpen,
    onClose: onCloseReactionUsers,
    onOpen: onOpenReactionUsers,
  } = useDisclosure();

  const {
    isOpen: createShareContentIsOpen,
    onClose: onCloseShareContent,
    onOpen: onOpenShareContent,
  } = useDisclosure();

  const {
    isOpen: editCommentModalIsOpen,
    onClose: onCloseEditCommentModal,
    onOpen: onOpenEditCommentModal,
  } = useDisclosure();

  const goToPost = () => {
    const params = {};
    location.search
      .replace('?', '')
      .split('&')
      .forEach((x) => {
        let arr = x.split('=');
        params[arr[0]] = arr[1];
      });
    if (!post?.reply_to && !isPublic) {
      navigate(postLink || `/post?id=${params['id']}&commentId=${post.id}`);
    }
  };

  const likePost = () => {
    try {
      if (isPublic) return;
      mutate(
        {
          key: 'react/post',
          method: 'post',
          data: {
            post_id: parentId,
            comment_id: post.id,
          },
        },
        {
          onSuccess(res) {
            // addToast(res);
            isLiked
              ? setLikesCount((prev) => prev - 1)
              : setLikesCount((prev) => prev + 1);
            setIsLiked((prev) => !prev);
          },
          onError(e) {
            // addToast({message: 'Post was not liked'}, "error")
            console.error(e);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setIsLiked(post.isLiked);
    setLikesCount(post.likes_count);
    setCommentsCount(post.replies_count);
    setLatestReactionUser(post.latestReactionUser);
    const _message = `Hi! ${post.user.first_name} ${post.user.last_name} shared their thoughts on SeedAFounder; Follow the conversation`;
    const _link = `${process.env.REACT_APP_FRONTEND_URL}public/post?id=${parentId}`;
    setMessage(_message);
    setLink(_link);
  }, [post]);

  const onCreateComment = (comment) => {
    queryClient.invalidateQueries(queryKey);
    setCommentsCount((prev) => prev + 1);
    onCreateReply(comment);
  };

  const openModal = (e) => {
    e.stopPropagation();
    if (!post.reply_to) {
      createCommentModalIsOpen ? onCloseCreateCommentModal() : onOpenCreateCommentModal();
    };
  };

  useEffect(() => {
    if (location.pathname.includes('public')) {
      setIsPublic(true);
    } else {
      setIsPublic(false);
    }
  }, [location]);

  const getTimeElapsed = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInSeconds = Math.floor((now - created) / 1000);

    const intervals = {
      y: 31536000,
      w: 604800,
      d: 86400,
      h: 3600,
      m: 60,
      s: 1,
    };

    // Find the largest time unit that fits
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const value = Math.floor(diffInSeconds / secondsInUnit);
      if (value >= 1) {
        return `${value}${unit}`;
      }
    }

    return 'just now';
  };

  const onSuccess = () => {
    // queryClient.invalidateQueries(queryKey);
    onCommentSuccess();
  };

  const renderTextWithNameLink = (text) => {
    const fullName = `${post?.parentComment?.user?.first_name || post?.user?.first_name} ${post?.parentComment?.user?.last_name || post?.user?.last_name}`;

    const parts = text.split(new RegExp(`(${fullName})`, 'g'));

    return parts.map((part, idx) =>
      part === fullName ? (
        <a
          key={idx}
          style={{ color: '#3182ce', textDecoration: 'underline' }}
          onClick={(e) => {
            e.stopPropagation();
            const userType =
              post?.parentComment?.user?.user_type_id ||
              post?.user?.user_type_id;
            const userId = post?.parentComment?.user?.id || post?.user_id;
            navigate(`../view-user?userid=${userId}&usertype=${userType}`);
          }}
        >
          {fullName}
        </a>
      ) : (
        part
      ),
    );
  };

  return (
    <Box className="flex cursor-pointer items-start gap-2 p-2 pt-5">
      <ShareContentModal
        isOpen={createShareContentIsOpen}
        onClose={onCloseShareContent}
        message={message}
        link={link}
        subject={'Hello! Check out this post on SeedAFounder.'}
      />

      <DeleteCommentModal
        isOpen={deleteCommentModalIsOpen}
        onClose={onCloseDeleteCommentModal}
        comment={post}
        onSuccess={onSuccess}
      />

      <EditCommentModal
        isOpen={editCommentModalIsOpen}
        onClose={onCloseEditCommentModal}
        comment={post}
        onSuccess={onSuccess}
      />

      <ReactionUsers
        reactionUsers={reactionUsers}
        isVisible={reactionUsersIsOpen}
        onClose={onCloseReactionUsers}
        isLoading={reactionUsersLoading}
      />

      <Box>
        <Avatar
          src={post.user.profile_image ?? null}
          name={`${post.user.first_name} ${post.user.last_name}`}
          size={'sm'}
        />
      </Box>

      <Box
        onClick={goToPost}
        className="w-full cursor-pointer flex px-2 flex-col"
      >
        <Box className="w-full pb-5 min-h-10">
          <Box className="flex items-center justify-between">
            <Box className="flex flex-row items-center gap-1">
              <Text as="span" className="font-medium text-[#134a70] text-sm capitalize">
                {post.user.first_name} {post.user.last_name}
              </Text>
            </Box>
            <Box className="flex items-center gap-2">
              {/* Edit/Delete Menu - Only show for comment owner */}
              {post?.user_id === userInfo?.id && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<FaEllipsisV className="text-gray-600 mx-auto" />}
                    variant="unstyled"
                    size="sm"
                    aria-label="Comment options"
                    className="hover:bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200"
                    _active={{ bg: 'gray-200' }}
                    _hover={{ bg: 'gray-100' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MenuList
                    className="shadow-xl border border-gray-100 rounded-xl py-1"
                    minW="200px"
                  >
                    <MenuItem
                      icon={<FaPen className="text-gray-600" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenEditCommentModal();
                      }}
                      className="px-4 py-2.5 hover:bg-gray-50 transition-colors duration-150 font-medium"
                    >
                      Edit
                    </MenuItem>
                    <MenuItem
                      icon={<FaTrash className="text-gray-600" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenDeleteCommentModal();
                      }}
                      className="px-4 py-2.5 hover:bg-gray-50 transition-colors duration-150 text-red-600 font-medium"
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Box>
          </Box>
          <Box className="flex items-center gap-2">
            <Text className="text-xs">{moment(post.createdAt).fromNow()}</Text>

              <FaDotCircle className="text-[7px]" />
              <Text
                as="span"
                className="font-semibold text-xs text-gray-600 capitalize"
              >
                {post.user.user_type_id == 1
                  ? 'Founder'
                  : post.user.user_type_id == 2
                    ? 'Investor'
                    : post.user.user_type_id == 3
                      ? 'Expert'
                      : 'Member'}
              </Text>
          </Box>
          <Text className="w-full" color="#000">
            {post?.text.split('\n\n').map((text, i) => (
              <span key={i}>
                {renderTextWithNameLink(text)}
                <br />
              </span>
            ))}
          </Text>
        </Box>

        <ReactionDisplay
          latestReactionUser={latestReactionUser}
          likesCount={likesCount}
          onClickReactionUsers={() => {
            refetchReactionUsers();
            onOpenReactionUsers();
          }}
        />

        <Box className="flex items-center justify-between">

          <Box onClick={(e) => {
                  e.stopPropagation();
                  likePost();
                }} className="flex gap-1 cursor-pointer items-center">
            <Box className="text-sm">
              👍
            </Box>
            <Text>{likesCount}</Text>
          </Box>
          <Box
            className={`flex gap-1 cursor-pointer items-center ${post.reply_to ? 'text-gray-300' : 'text-gray-600'}`}
          >
            <FaRegComment onClick={openModal} />
            <Text className={`${post.reply_to ? 'invisible' : 'visible'}`}>
              {commentsCount}
            </Text>
          </Box>
          <Box
            onClick={(e) => {
              onOpenShareContent();
            }}
            className="flex gap-1 cursor-pointer items-center"
          >
            <FaShareAlt />
          </Box>
        </Box>

        {createCommentModalIsOpen ? (<>
        
        <CreateReply
          isOpen={createCommentModalIsOpen}
          onClose={onCloseCreateCommentModal}
          comment={post}
          postId={parentId}
          onSuccess={onCreateComment}
          showPost={false}
          showAltReplyButton={true}
          showCloseButton={false}
          showReplyButton={false}
        />
      </>) : null}
      </Box>
    </Box>
  );
};

export default Comment;
